import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { clinicalScoreAPI } from '@/utils/api';
import { ArrowRight, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ClinicalScoreWidget = () => {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchScore();
  }, []);

  const fetchScore = async () => {
    try {
      const response = await clinicalScoreAPI.getMyScore(30);
      setScore(response.data);
    } catch (error) {
      console.error('Error fetching score:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColorClass = (color) => {
    const colors = {
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      yellow: 'bg-yellow-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  const getTextColorClass = (color) => {
    const colors = {
      green: 'text-green-400',
      blue: 'text-blue-400',
      yellow: 'text-yellow-400',
      orange: 'text-orange-400',
      red: 'text-red-400'
    };
    return colors[color] || 'text-gray-400';
  };

  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-100">Clinical Co-Parenting Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!score) {
    return null;
  }

  return (
    <Card className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-all">
      <CardHeader>
        <CardTitle className="text-slate-100 flex items-center justify-between">
          <span>Clinical Co-Parenting Score</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/clinical-score')}
            className="text-blue-400 hover:text-blue-300"
          >
            View Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Overall Score Display */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="w-32 h-32 rounded-full border-8 border-slate-700 flex items-center justify-center">
                <div className="text-center">
                  <div className={`text-4xl font-bold ${getTextColorClass(score.color)}`}>
                    {score.overall_score}
                  </div>
                  <div className="text-xs text-slate-400">out of 100</div>
                </div>
              </div>
              <div className={`absolute -top-2 -right-2 ${getColorClass(score.color)} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                {score.rating}
              </div>
            </div>
          </div>

          {/* Quick Breakdown */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-700/50 p-3 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Communication</div>
              <div className="text-lg font-semibold text-slate-100">
                {score.breakdown.tone.score}/100
              </div>
              <div className="text-xs text-slate-400">
                {score.breakdown.tone.negative} negative msgs
              </div>
            </div>

            <div className="bg-slate-700/50 p-3 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Check-Ins</div>
              <div className="text-lg font-semibold text-slate-100">
                {score.breakdown.checkin.score}/100
              </div>
              <div className="text-xs text-slate-400">
                {score.breakdown.checkin.completion_rate}% completed
              </div>
            </div>

            <div className="bg-slate-700/50 p-3 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Response Time</div>
              <div className="text-lg font-semibold text-slate-100">
                {score.breakdown.response.score}/100
              </div>
              <div className="text-xs text-slate-400">
                {score.breakdown.response.avg_response_hours}h avg
              </div>
            </div>

            <div className="bg-slate-700/50 p-3 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Video Calls</div>
              <div className="text-lg font-semibold text-slate-100">
                {score.breakdown.video.score}/100
              </div>
              <div className="text-xs text-slate-400">
                {score.breakdown.video.attendance_rate}% attended
              </div>
            </div>
          </div>

          {/* Alert for Low Score */}
          {score.overall_score < 60 && (
            <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-3 flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5" />
              <div className="text-sm text-orange-300">
                Your score needs attention. Click "View Details" to see recommendations for improvement.
              </div>
            </div>
          )}

          {/* Action Button */}
          <Button
            onClick={() => navigate('/clinical-score')}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            View Full Report
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClinicalScoreWidget;
